import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import API from '../../api';
import { SnackbarContext } from '../../contexts';

export const useCredit = () => {
  const { updateSnackbar } = useContext(SnackbarContext);
  const { t } = useTranslation();
  const history = useHistory();
  const [credit, setCredit] = useState(null);

  const getIssue = (seq) => {
    if (seq) {
      API.Credit.GetIssue(seq)
        .then((res) => {
          setCredit(res.data);
        })
        .catch((err) => {
          updateSnackbar({
            type: 'error',
            open: true,
            message: err.data?.message || '조회 중 오류가 발생했습니다.',
          });
        });
    }
  };

  const getMove = (seq) => {
    if (seq) {
      API.Credit.GetMove(seq)
        .then((res) => {
          setCredit(res.data);
        })
        .catch((err) => {
          updateSnackbar({
            type: 'error',
            open: true,
            message: err.data?.message || '조회 중 오류가 발생했습니다.',
          });
        });
    }
  };

  const getOffset = (seq) => {
    if (seq) {
      API.Credit.GetOffset(seq)
        .then((res) => {
          setCredit(res.data);
        })
        .catch((err) => {
          updateSnackbar({
            type: 'error',
            open: true,
            message: err.data?.message || '조회 중 오류가 발생했습니다.',
          });
        });
    }
  };

  const getRetire = (seq) => {
    if (seq) {
      API.Credit.GetRetire(seq)
        .then((res) => {
          setCredit(res.data);
        })
        .catch((err) => {
          updateSnackbar({
            type: 'error',
            open: true,
            message: err.data?.message || '조회 중 오류가 발생했습니다.',
          });
        });
    }
  };

  const getDetail = (seq) => {
    if (seq) {
      API.Credit.GetDetail(seq)
        .then((res) => {
          setCredit(res.data);
        })
        .catch((err) => {
          updateSnackbar({
            type: 'error',
            open: true,
            message: err.data?.message || '조회 중 오류가 발생했습니다.',
          });
        });
    }
  };

  const postIssue = (data) => {
    API.Credit.PostIssue(data)
      .then((res) => {
        const result = res.data;
        history.push(`/request_complete?type=credit&mode=issue`);
        return result;
      })
      .catch((err) => {
        updateSnackbar({
          type: 'error',
          open: true,
          message: err.data?.message || '저장 중 오류가 발생했습니다.',
        });
        return false;
      });
  };

  const putIssue = (seq, data) => {
    return API.Credit.PutIssue(seq, data)
      .then((res) => {
        updateSnackbar({
          type: 'success',
          open: true,
          message: t('UPDATED'),
        });
        return true;
      })
      .catch((err) => {
        updateSnackbar({
          type: 'error',
          open: true,
          message: err.data?.message || '수정 중 오류가 발생했습니다.',
        });
        return false;
      });
  };

  const fileUpload = async (file, path) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('path', path);

    return API.Common.PostFile(formData).then((res) => {
      return res.data.cloudUrl;
    });
  };

  const postMove = async (data) => {
    try {
      if (data.files && data.files.length > 0) {
        const fileUrls = await Promise.all(
          data.files.map(async (file) => {
            const fileUrl = await fileUpload(file, 'credit/move');
            return fileUrl;
          }),
        );
        data.files = fileUrls;
      }

      const { companyNameAssignee, assigneeUserCompanyName, ...rest } = data;
      const res = await API.Credit.PostMove(rest);
      history.push(`/request_complete?type=credit&mode=move`);
      return res.data;
    } catch (err) {
      updateSnackbar({
        type: 'error',
        open: true,
        message: err.data?.message || '저장 중 오류가 발생했습니다.',
      });
      return false;
    }
  };

  const putMove = async (seq, data) => {
    try {
      // 파일 업로드 처리
      if (data.files && data.files.length > 0) {
        const fileUrls = await Promise.all(
          data.files.map(async (file) => {
            // File 객체인 경우에만 업로드
            if (file instanceof File) {
              const fileUrl = await fileUpload(file, 'credit/move');
              return fileUrl;
            }
            // 이미 URL인 경우 그대로 반환
            return file;
          }),
        );
        data.files = fileUrls;
      }

      const { companyNameAssignee, assigneeUserCompanyName, ...rest } = data;
      await API.Credit.PutMove(seq, rest);
      updateSnackbar({
        type: 'success',
        open: true,
        message: t('UPDATED'),
      });
      return true;
    } catch (err) {
      updateSnackbar({
        type: 'error',
        open: true,
        message: err.data?.message || '수정 중 오류가 발생했습니다.',
      });
      return false;
    }
  };

  const postOffset = (data) => {
    API.Credit.PostOffset(data)
      .then((res) => {
        const result = res.data;
        history.push(`/request_complete?type=credit&mode=offset`);
        return result;
      })
      .catch((err) => {
        updateSnackbar({
          type: 'error',
          open: true,
          message: err.data?.message || '저장 중 오류가 발생했습니다.',
        });
        return false;
      });
  };

  const putOffset = (seq, data) => {
    return API.Credit.PutOffset(seq, data)
      .then((res) => {
        updateSnackbar({
          type: 'success',
          open: true,
          message: t('UPDATED'),
        });
        return true;
      })
      .catch((err) => {
        updateSnackbar({
          type: 'error',
          open: true,
          message: err.data?.message || '수정 중 오류가 발생했습니다.',
        });
        return false;
      });
  };

  const postRetire = (data) => {
    API.Credit.PostRetire(data)
      .then((res) => {
        const result = res.data;
        history.push(`/request_complete?type=credit&mode=retire`);
        return result;
      })
      .catch((err) => {
        updateSnackbar({
          type: 'error',
          open: true,
          message: err.data?.message || '저장 중 오류가 발생했습니다.',
        });
        return false;
      });
  };

  const putRetire = (seq, data) => {
    return API.Credit.PutRetire(seq, data)
      .then((res) => {
        updateSnackbar({
          type: 'success',
          open: true,
          message: t('UPDATED'),
        });
        return true;
      })
      .catch((err) => {
        updateSnackbar({
          type: 'error',
          open: true,
          message: err.data?.message || '수정 중 오류가 발생했습니다.',
        });
        return false;
      });
  };

  const getAssignee = (params) => {
    return API.Credit.GetAssignee(params)
      .then((res) => {
        return res.data;
      })
      .catch((err) => {
        updateSnackbar({
          type: 'error',
          open: true,
          message: err.data?.message || '조회 중 오류가 발생했습니다.',
        });
        return null;
      });
  };

  return {
    credit,
    getIssue,
    getMove,
    getOffset,
    getRetire,
    getDetail,
    postIssue,
    putIssue,
    postMove,
    putMove,
    postOffset,
    putOffset,
    postRetire,
    putRetire,
    getAssignee,
  };
};
